import { ColumnOptions } from '@/views/components/DragAndDrop/ColumnOptions';
import { NOTIFICATION_TYPES, showNotification } from '@/views/components/Modals/notification';
import { Col, Divider, Row } from 'antd';
import { camelCase, cloneDeep } from 'es-toolkit';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import {
  getLDClaimsSortingFieldsInProgress,
  saveLDClaimsTableViewColumnsInProgress,
} from '../../../../../redux/Claims/LossAndDamage/LossAndDamageClaimsActions';
import Button from '../../../../components/Buttons/Button';

const LDClaimTableViewFieldsDrawer = ({ onClose }) => {
  const [selectedColumns, updateSelectedColumns] = useState([]);
  const dispatch = useDispatch();
  const sortingFields = useSelector((state) => state.lossAndDamage.sortingFields, shallowEqual);
  const tableViewColumns = useSelector((state) => state.lossAndDamage.tableViewColumns);
  const saveTableViewColumnsProgressing = useSelector(
    (state) => state.lossAndDamage.saveTableViewColumns.progressing,
    shallowEqual,
  );

  useEffect(() => {
    dispatch(getLDClaimsSortingFieldsInProgress());
  }, [dispatch]);

  useEffect(() => {
    updateSelectedColumns(tableViewColumns?.data ? tableViewColumns.data : []);
  }, [tableViewColumns?.data]);

  const availableColumns = useMemo(() => {
    let data = [];
    if (sortingFields?.data?.length > 0) {
      data = sortingFields?.data?.map(({ value, key }) => ({
        label: value,
        value: camelCase(key),
      }));
    } else {
      data = [];
    }

    const filteredData = data.filter(({ value }) => value !== 'claimNumber');

    return filteredData;
  }, [sortingFields]);

  const selectedColumnValuesArray = useMemo(
    () => selectedColumns.map(({ columnValue }) => columnValue),
    [selectedColumns],
  );

  const handleOnDragEnd = useCallback((result) => {
    if (!result) return;
    updateSelectedColumns(result);
  }, []);

  const handleAddNewColumn = () => {
    const selectedColumnsLocal = cloneDeep(selectedColumns);
    selectedColumnsLocal.push({
      columnName: undefined,
      columnValue: undefined,
      id: uuidv4(),
    });
    updateSelectedColumns(selectedColumnsLocal);
  };

  const handleRemoveColumn = (index) => {
    const selectedColumnsLocal = cloneDeep(selectedColumns);
    selectedColumnsLocal.splice(index, 1);
    updateSelectedColumns(selectedColumnsLocal);
  };

  const handleSelectValueChange = useCallback(
    (val, index) => {
      const selectedColumnsLocal = cloneDeep(selectedColumns);
      selectedColumnsLocal[index].columnName = val.label;
      selectedColumnsLocal[index].columnValue = val.value;
      updateSelectedColumns(selectedColumnsLocal);
    },
    [selectedColumns],
  );

  const hasValidationErrors = (columns) => {
    return columns.some((col) => !col.columnName || !col.columnValue);
  };

  const handleSaveColumns = () => {
    if (hasValidationErrors(selectedColumns)) {
      showNotification({
        type: NOTIFICATION_TYPES.ERROR,
        message: 'Cannot add a column without selecting a column name',
      });
      return;
    }
    const handleDone = () => {
      onClose();
    };
    dispatch(
      saveLDClaimsTableViewColumnsInProgress({
        data: selectedColumns,
        handleDone,
      }),
    );
  };

  return (
    <Row className="h-100 overflow-hidden flex-direction-column">
      <Col className="flex-1 py-15 overflow-hidden">
        <div className="px-15 h-100 custom-scroll-bar">
          <ColumnOptions
            availableColumns={availableColumns}
            isColumnsLoading={tableViewColumns.progressing}
            handleOnDragEnd={handleOnDragEnd}
            selectedColumns={selectedColumns}
            handleAddNewColumn={handleAddNewColumn}
            handleRemoveColumn={handleRemoveColumn}
            handleSelectValueChange={handleSelectValueChange}
            selectedColumnValuesArray={selectedColumnValuesArray}
          />
        </div>
      </Col>
      <Col>
        <Divider className="my-15 border-bottom-border" />
      </Col>
      <Col className="px-15 pb-15">
        <Row justify="end" gutter={15}>
          <Col>
            <Button variant="danger" type="default" onClick={() => onClose()}>
              Cancel
            </Button>
          </Col>
          <Col>
            <Button onClick={handleSaveColumns} loading={saveTableViewColumnsProgressing}>
              Save
            </Button>
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export default LDClaimTableViewFieldsDrawer;
